import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';
import { ReactNode } from 'react';

import { Bem } from '../../service/Bem/Bem';
import {
    formatNumber,
    parseNumber
} from '../../service/FormatHelpers/FormatHelpers';
import {
    validateCpmBannerPhraseBid,
    validatePhraseBid
} from '../../service/PhraseMoneyCell/PhraseMoneyCell';
import { ThemePopup } from '../../typings/common';
import { CoverageByPriceData, PriceConstants } from '../../typings/domain';
import { CurrencyCode } from '../../typings/graphql-grid';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { ContextPhrasePriceEditor } from '../ContextPhrasePriceEditor/ContextPhrasePriceEditor';
import { ErrorItemText } from '../ErrorsPresenter/ErrorsPresenter';
import { PopupDecorator } from '../PopupDecorator/PopupDecorator';

import './ContextPhrasePriceEditorPopup.css';
import * as keyset from './ContextPhrasePriceEditorPopup.i18n';

const i18n = i18nFactory(keyset);

interface ContextPhrasePriceEditorPopupProps {
    priceContext?: number;
    priceForCoverage: Array<CoverageByPriceData>;
    currency: CurrencyCode;
    onSave: (value: number) => void;
    anchor: ReactNode;
    isSaving: boolean;
    isPopupOpen: boolean;
    onCancel: () => void;
    error?: string;
    priceLimit: PriceConstants;
    isCpmBanner: boolean;
}

interface ContextPhrasePriceEditorPopupState {
    priceContext: string;
}

export class ContextPhrasePriceEditorPopup extends React.Component<
    ContextPhrasePriceEditorPopupProps,
    ContextPhrasePriceEditorPopupState
> {
    constructor(props: ContextPhrasePriceEditorPopupProps) {
        super(props);

        this.state = {
            priceContext: formatNumber(props.priceContext || 0, 2)
        };
    }

    componentWillReceiveProps(nextProps: ContextPhrasePriceEditorPopupProps) {
        // Если нет ошибок и нет загрузки - сохранение прошло успешно - закрываем попап
        if (!nextProps.error && !nextProps.isSaving) {
            this.cancel();
        }
    }

    render() {
        const { isPopupOpen, anchor } = this.props;

        return (
            <PopupDecorator
                onOutsideClick={this.cancel}
                anchor={anchor}
                visible={isPopupOpen}
                target="anchor"
                theme={ThemePopup.CLEAR}
                directions={[
                    'bottom-center',
                    'top-center',
                    'left-center',
                    'right-center'
                ]}
                footer={this.getFooterContent()}
                hasTail={true}
            >
                {this.getPopupContent()}
            </PopupDecorator>
        );
    }

    private onChange = (priceContext: string) => {
        this.setState({
            priceContext
        });
    };

    private getWarning = (): string | undefined => {
        const { priceLimit } = this.props;
        const { priceContext } = this.state;
        const { bigRate, maxPrice } = priceLimit;
        const parsedPrice = parseNumber(priceContext);

        if (parsedPrice > bigRate && parsedPrice < maxPrice) {
            return i18n('highest-bid');
        }

        return undefined;
    };

    private getError = (): ErrorItemText | undefined => {
        const {
            error: serverError,
            priceLimit,
            isCpmBanner,
            currency
        } = this.props;
        const { priceContext } = this.state;

        if (serverError) {
            return serverError;
        }

        const clientValidationResult = isCpmBanner
            ? validateCpmBannerPhraseBid(priceContext, priceLimit, currency)
            : validatePhraseBid(priceContext, priceLimit, currency);

        if (clientValidationResult.errors) {
            return clientValidationResult.errors[0].text;
        }

        return undefined;
    };

    private onSave = () => {
        const { onSave, priceContext } = this.props;
        const { priceContext: newPriceContext } = this.state;
        const parsedNewPriceContext = parseNumber(newPriceContext);

        this.setFormatedText();

        if (priceContext === parsedNewPriceContext) {
            this.props.onCancel();
            return;
        }

        if (!this.getError()) {
            onSave(parsedNewPriceContext);
        }
    };

    private onPriceContextBlur = () => {
        const parsedNumber = parseNumber(this.state.priceContext);
        if (!isNaN(parsedNumber)) {
            this.setState({
                priceContext: formatNumber(parsedNumber, 2)
            });
        }
    };

    private getPopupContent = () => {
        const { priceContext } = this.state;
        const { currency, priceForCoverage, isCpmBanner } = this.props;

        return (
            <ContextPhrasePriceEditor
                priceContext={priceContext}
                priceForCoverage={priceForCoverage}
                error={this.getError()}
                priceLimitWarning={this.getWarning()}
                currency={currency}
                onChange={this.onChange}
                onBlur={this.onPriceContextBlur}
                isCpmBanner={isCpmBanner}
            />
        );
    };

    private cancel = () => {
        const { priceContext, onCancel } = this.props;

        this.setState({
            priceContext: priceContext ? formatNumber(priceContext, 2) : ''
        });

        onCancel();
    };

    private setFormatedText = () => {
        const { priceContext } = this.state;

        const formatedPriceContext = formatNumber(parseNumber(priceContext), 2);

        this.setState({
            priceContext: formatedPriceContext
        });
    };

    private getFooterContent = () => {
        const { isSaving } = this.props;

        return (
            <>
                <ButtonDecorator
                    theme={'action'}
                    size={'n'}
                    view="default"
                    tone="default"
                    text={i18n('button-submit')}
                    disabled={Boolean(this.getError())}
                    progress={isSaving}
                    onClick={this.onSave}
                />
                <ButtonDecorator
                    theme={'pseudo'}
                    size={'n'}
                    view="default"
                    tone="default"
                    text={i18n('button-cancel')}
                    onClick={this.cancel}
                    cls={Bem.cls(
                        'context-phrase-price-editor-popup',
                        'button-cancel'
                    )}
                />
            </>
        );
    };
}
